import { getFizzNodeById as getIndexedFizzNodeById } from "@/services/subgraph";
import { ResourceType } from "@/components/register-fizz-node/resources-section";
import { networkType, RPC_MAP } from "@/config";
import EscrowProtocolAbi from "@/contracts/abis/EscrowProtocol.json";
import spheronSdk from "@/libs/spheron-sdk";
import { notFound } from "next/navigation";
import {
  compareVersions,
  fetchWithRedirect,
  sortLeaderboardFizzNodes,
} from "@/utils";
import { getAllFizzNodesForProvider, getFizzNodeStatus } from "./status";
import { getFizzNodeRewardsPerEra, getTotalRewards } from "./rewards";
import { getProviderVersion } from "./version";
import { getNodes } from "./indexer";
import { INode } from "@/app/api/cache/route";
import Regions from "@/config/region-config";
import { getPartnerCodeStatus } from "./partner-code";
import { getChallengerData } from "./challenger";
import { FizzParams } from "@/app/[fizzAddress]/register/types";
import { Contract, JsonRpcProvider } from "ethers";
import { addressMap } from "@/contracts/addresses";

export const getAllNodes = async () => {
  try {
    return await spheronSdk.fizz.getAllFizzNodes();
  } catch (error) {
    console.log("Error in getting fizz nodes", error);
  }
};

export const getFizzNodeByAddress = async (address: string) => {
  try {
    const node = await spheronSdk.fizz.getFizzNodeByAddress(address);
    return node;
  } catch (error) {
    console.log("Error in fizz node by address", error);
    throw error; // Rethrow the error to allow it to be handled by the caller
  }
};

export const getFizzEarnings = async (
  fizzId: string,
  tokenAddress: string
): Promise<{ earned: string; withdrawn: string; balance: string }> => {
  try {
    const provider = new JsonRpcProvider(RPC_MAP[networkType].http);
    const escrowContract = new Contract(
      addressMap[networkType].escrowProtocol,
      EscrowProtocolAbi,
      provider
    );

    const response = await escrowContract.fizzNodeEarnings(
      BigInt(fizzId),
      tokenAddress
    );

    return {
      earned: response.earned.toString(),
      withdrawn: response.withdrawn.toString(),
      balance: response.balance.toString(),
    };
  } catch (error) {
    console.log("Error in getting earnings", error);
    throw error; // Rethrow the error to allow it to be handled by the caller
  }
};

export const updateFizzNodeName = async (name: string) => {
  try {
    return await spheronSdk.fizz.updateFizzName(name);
  } catch (error) {
    console.log("Error in updating nodes", error);
  }
};

export const getNodeEarnings = async (
  fizzAddress: string,
  tokenAddress: string
) => {
  try {
    return await spheronSdk.fizz.getFizzEarnings(fizzAddress, tokenAddress);
  } catch (error) {
    console.log("Error in gettings earnings", error);
  }
};

export const getFizzLeases = async (fizzId: string, providerId: string) => {
  try {
    const response = await spheronSdk.fizz.getFizzLeases(
      BigInt(fizzId),
      BigInt(providerId)
    );
    return response as any;
  } catch (error) {
    console.log("Error in gettings leases", error);
  }
};

export const getProviderDetails = async (providerId: string) => {
  try {
    return await spheronSdk.fizz.getProvider(BigInt(providerId));
  } catch (error) {
    console.log("Error in gettings provider details", error);
    throw error;
  }
};

export const getFizzNodeById = async (
  fizzNode: string
): Promise<{
  fizzId: string;
  providerId: string;
  name: string;
  region: string;
  spec: string;
  walletAddress: string;
  paymentsAccepted: string[];
  status: string;
  joinTimestamp: string;
  rewardWallet: string;
}> => {
  try {
    const node = await spheronSdk.fizz.getFizzById(BigInt(fizzNode));
    return node as {
      fizzId: string;
      providerId: string;
      name: string;
      region: string;
      spec: string;
      walletAddress: string;
      paymentsAccepted: string[];
      status: string;
      joinTimestamp: string;
      rewardWallet: string;
    };
  } catch (error) {
    console.log("Error in fizz node by address", error);
    throw error;
  }
};

export const withdrawEarnings = async (
  fizzId: string,
  tokenAddress: string,
  amount: number,
  onSuccessCallback: (data: unknown) => void,
  onFailureCallback: (data: unknown) => void
) => {
  try {
    // const decimals =
    //   getTokenDetails(fizzId, networkType as NetworkType)?.decimal || 18;
    const provider = new JsonRpcProvider(RPC_MAP[networkType].http);
    const escrowContract = new Contract(
      addressMap[networkType].escrowProtocol,
      EscrowProtocolAbi,
      provider
    );

    const response = await escrowContract.withdrawEarnings(
      "",
      fizzId,
      tokenAddress,
      amount
    );
    onSuccessCallback(response);
    return response;
  } catch (error) {
    onFailureCallback(error);
    console.log("Error in withdrawing", error);
    throw error;
  }
};

export const addFizzNode = async (payload: {
  fizzNodeInfo: FizzParams;
  isStellar: boolean;
  transactionDetails: null | {
    transactionHash: string;
    chainId: number;
    chainName: string;
    walletAddress: string;
  };
  partnerCode?: string;
}) => {
  try {
    const response = await fetchWithRedirect(`/api/add-fizz-node`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    const json = await response.json();
    return json;
  } catch (error) {
    console.error("Error adding fizz node -> ", error);
    throw error;
  }
};

export const updateFizzSpecs = async (payload: {
  spec: string;
  fizzId: string;
  fizzAddress: string;
}) => {
  try {
    const response = await fetchWithRedirect(`/api/update-fizz-spec`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error);
    return json;
  } catch (error) {
    console.error("Error updating fizz specs -> ", error);
    throw error;
  }
};

export const getAllProviders = async () => {
  try {
    const responses = await spheronSdk.fizz.getAllProviders();

    // Filter out providers with localhost hostUri or no name
    const filteredResponses = responses.filter(
      (response: any) =>
        response["hostUri"] !== "localhost" &&
        response["hostUri"] &&
        response["region"] !== "dev-spheron" &&
        response["region"] &&
        response["walletAddress"].toLowerCase() !==
          "0xf8937fd60fd0068f68b508ad3e32b8cf41509eca" &&
        !response["hostUri"].includes("fluence") &&
        !response["hostUri"].includes("nebulablock") /* &&
        response["name"].trim() !== "" */
    );

    // Apply search filter
    // const searchFilteredResponses = filteredResponses.filter(response =>
    //   response[1].toLowerCase().includes(searchTerm.toLowerCase()) || // name
    //   response[3].toLowerCase().includes(searchTerm.toLowerCase())    // address
    // );

    // Process all providers to get their details including total rewards
    const allProviderDetails = await Promise.all(
      filteredResponses.map(async (response: any) => {
        // let specs: StatusResponse = {
        //   totalCPUs: 0,
        //   totalMemory: 0,
        //   totalStorage: 0,
        //   gpuInfos: [],
        // };
        let version = "-";
        if (response.status.toString() === "Active") {
          // specs = await getProviderStatus(response[6]);
          version = await getProviderVersion(response.hostUri);
        }
        return {
          providerId: response.providerId.toString(),
          name: response.name,
          region: response.region,
          walletAddress: response.walletAddress,
          hostUri: response.hostUri,
          status: response.status,
          tier: response.tier,
          // specs,
          // perEraRewardData,
          // totalRewardsData,
          timestamp: Number(response.joinTimestamp.toString()),
          version,
        };
      })
    );

    const activeProviders = allProviderDetails.filter((provider: any) => {
      if (provider.version !== "-" && (provider.status as any) === "Active") {
        if (compareVersions(provider.version.substring(1), "1.3.0") >= 0) {
          return true;
        }
      }
      return false;
    });

    const sortedProviders = activeProviders.sort(
      (a: any, b: any) => Number(a.tier) - Number(b.tier)
    );

    // Calculate total pages after filtering and sorting
    const totalCount = sortedProviders.length;

    console.log("SORTED PROVIDERS", sortedProviders);

    return {
      providers: sortedProviders,
      totalCount,
      // pageSize,
      // pageNumber,
      // totalPages,
    };
  } catch (error) {
    console.error("Error fetching all providers -> ", error);
    throw error;
  }
};

export const fetchUniqueProvidersStatus = async (providerIds: string[]) => {
  try {
    const providerResponses = await Promise.all(
      providerIds.map(async (id) => {
        if (typeof id !== "undefined") {
          const providerResponse = await spheronSdk.fizz.getProvider(
            BigInt(id)
          );
          return { id, ...providerResponse };
        }
      })
    );

    const pResponses = providerResponses.filter(
      (provider) => typeof provider !== "undefined"
    );

    const filteredProviderIds = pResponses.filter((p) => {
      return p.hostUri && !p.hostUri?.includes("localhost");
    });

    const providerDetails = await Promise.all(
      filteredProviderIds.map(async (pd) => {
        return await getAllFizzNodesForProvider(pd.hostUri);
      })
    );

    const providerIdDetailsMap = new Map(
      filteredProviderIds.map((provider, i) => [
        provider.id,
        providerDetails[i],
      ])
    );

    return providerIdDetailsMap;
  } catch (error) {
    console.log("Error in getting status of provider", error);
    throw error;
  }
};

export const getAllFizzNodes = async (
  page?: number,
  limit?: number,
  search?: string
) => {
  const regions = Regions.map((region) => region.value);
  try {
    const dbNodes: { nodes: INode[]; totalNodes: number } = await getNodes(
      page,
      limit,
      search
    );

    const providerIdSet = new Set();
    const filteredNodes = dbNodes.nodes.filter(
      (node) =>
        node.providerId?.toString() !== "0" &&
        typeof node.providerId !== "undefined"
    );

    filteredNodes.forEach((node) => {
      if (node.providerId?.toString() !== "0") {
        providerIdSet.add(node.providerId?.toString());
      }
    });

    const sortedFizzNodes = sortLeaderboardFizzNodes(dbNodes.nodes, regions);

    return {
      fizzNodes: sortedFizzNodes,
      totalCount: dbNodes.totalNodes,
      uniqueProviderIds: Array.from(providerIdSet),
      fromCache: true,
    };
  } catch (error) {
    console.log("Error in getting fizz node details -> ", error);
    notFound();
  }
};

export const listenToFizzNodeCreation = async (
  onSuccessCallback: (fizzId: bigint, walletAddress: string) => void,
  onFailureCallback: () => void
) => {
  try {
    const response = spheronSdk.fizz.listenToFizzCreated(
      onSuccessCallback,
      onFailureCallback
    );
    return response;
  } catch (error) {
    console.log("Error in listening to fizz node creation -> ", error);
    throw error;
  }
};

export const updateFizzProvider = async (payload: {
  providerId: string;
  fizzId: string;
  fizzAddress: string;
}) => {
  try {
    const response = await fetchWithRedirect(`/api/update-fizz-provider`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error);
    return json;
  } catch (error) {
    console.error("Error updating fizz node provider -> ", error);
    throw error;
  }
};

export const getAttributes = async (fizzAddress: string, category: string) => {
  try {
    const response = await spheronSdk.fizz.getAttributes(
      fizzAddress,
      category.toUpperCase()
    );
    return response;
  } catch (error) {
    console.log("Error is getting attributes", error);
    throw error;
  }
};

export const removeAttribute = async (category: string) => {
  try {
    const response = await spheronSdk.fizz.removeFizzAttribute(category);
    return response;
  } catch (error) {
    console.error("Error removing fizz attribute -> ", error);
    throw error;
  }
};

export const submitAttributes = async (
  resourceType: ResourceType,
  ids: bigint[],
  capacities: bigint[]
) => {
  try {
    const response: any = await spheronSdk.fizz.submitAttributes(
      resourceType,
      ids,
      capacities
    );
    if (response) {
      return response;
    }
    throw new Error("Error occured in updating resource attributes");
  } catch (error) {
    console.error("Error submitting fizz node attributes -> ", error);
    throw error;
  }
};

export const getAccountByAddress = async (address: string) => {
  try {
    const response = await fetch(
      `/api/cache?search=${address}&page=1&limit=15`,
      {
        method: "GET",
      }
    );
    const resjson = await response.json();

    if (resjson?.nodes?.length === 0) {
      return resjson;
    }

    const fizzNodeResponse = resjson?.nodes[0];

    const isRegistered = Number(fizzNodeResponse.status) === 1;

    const [
      perEraRewardData,
      totalRewardsData,
      fizzNodeData,
      lastFiveDaysChallenges,
      partnerCodeStatus,
      providerDetails,
    ] = await Promise.all([
      getFizzNodeRewardsPerEra(address, isRegistered),
      getTotalRewards(address, isRegistered),
      getIndexedFizzNodeById(String(fizzNodeResponse.id)),
      getChallengerData(address, -6, 5),
      getPartnerCodeStatus(address),
      getProviderDetails(fizzNodeResponse.providerId),
    ]);

    const isDeniedFizz = fizzNodeData?.isDenied;
    const orderedEras = [
      ...lastFiveDaysChallenges.challengesData.eras,
    ].reverse();
    const yesterdayEra = orderedEras[0];
    const pendingEras =
      yesterdayEra?.slashed &&
      yesterdayEra?.slashPending &&
      Number(totalRewardsData.toString()) !== 0
        ? 1
        : 0;

    const partnerMultiplier = partnerCodeStatus.hasPartnerCode
      ? partnerCodeStatus.partnerDetails.multiplier
      : 1;

    const perEraPoints = isDeniedFizz
      ? 0
      : perEraRewardData * partnerMultiplier;
    let totalPoints = isDeniedFizz
      ? 0
      : Number(totalRewardsData.toString()) - perEraRewardData * pendingEras;

    if (fizzNodeResponse.hasStellarBooster) {
      const pointsBeforeBooster = Number(
        fizzNodeResponse.pointsBeforeStellarBooster || "0"
      );
      totalPoints =
        (totalPoints - pointsBeforeBooster) * 1.5 + pointsBeforeBooster;
    } else if (partnerCodeStatus?.hasPartnerCode) {
      totalPoints *= partnerCodeStatus.partnerDetails.multiplier;
    }

    const fizzNodeStatus = await getFizzNodeStatus(
      providerDetails.hostUri,
      fizzNodeResponse.address
    );

    const newResJson = { ...resjson };
    newResJson.nodes[0] = {
      ...newResJson.nodes[0],
      // perEraRewardData: perEraPoints.toString(),
      // totalRewardsData: totalPoints.toString(),
      perEraRewardData: isDeniedFizz
        ? "0"
        : (
            lastFiveDaysChallenges.challengesData.rewardsPerEra *
            10 ** 18
          ).toString(),
      totalRewardsData: isDeniedFizz
        ? "0"
        : (lastFiveDaysChallenges.challengesData.points * 10 ** 18).toString(),
      status: fizzNodeStatus?.connected ? "2" : "3",
    };

    return newResJson;
  } catch (error) {
    console.error("Error while getting acc", error);
    throw error;
  }
};

export const getAllGatewayStatus = async () => {
  try {
    const response = await fetch("/api/gateway-status", {
      method: "GET",
    });
    const resjson = await response.json();
    return resjson;
  } catch (error) {
    console.error("Error while getting all gateway with status", error);
  }
};

export const updateNodeCache = async (nodeId: string, region: string) => {
  try {
    const response = await fetch(`/api/cache/update`, {
      method: "PATCH",
      body: JSON.stringify({ nodeId, region }),
    });
    const resjson = await response.json();
    return resjson;
  } catch (error) {
    console.error("Error updating node cache -> ", error);
    throw error;
  }
};

export const addRegisterFizzData = async (
  address: string,
  transactionHash: string,
  blockHash: string,
  chainId: string,
  chainName: string
) => {
  if (!address || !transactionHash || !blockHash) {
    console.error("Missing required parameters");
    return new Response(JSON.stringify({ error: true }), { status: 400 });
  }

  const response = await fetch(`/api/register-fizz`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      address: address.toLowerCase(),
      transactionHash: transactionHash.toLowerCase(),
      blockHash: blockHash.toLowerCase(),
      chainId: chainId.toLowerCase(),
      chainName,
    }),
  });

  return response;
};

export const fetchRegisterFizzData = async (address: string) => {
  if (!address) {
    return { error: true, message: "Address is required" };
  }

  const response = await fetch(
    `/api/register-fizz?address=${address.toLowerCase()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    return { error: true, message: "Failed to fetch data", details: errorData };
  }

  const data = await response.json();
  return { error: false, message: "", data: data };
};

export const updateFizzNode = async (payload: {
  spec: string;
  fizzId: string;
  fizzAddress: string;
}) => {
  try {
    const response = await fetchWithRedirect(`/api/update-fizz-node`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error);
    return json;
  } catch (error) {
    console.error("Error updating fizz specs -> ", error);
    throw error;
  }
};

export const fetchTestnetPoints = async (fizzAddress: string) => {
  try {
    const response = await fetch(
      `/api/get-testnet-points?address=${fizzAddress.toLowerCase()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        details: errorData,
        testnetTotalRewards: 0,
        fizzAddress,
        id: fizzAddress,
      } as const;
    }

    const data = (await response.json()) as {
      fizzAddress: string;
      id: string;
      testnetTotalRewards: number;
    };

    return { error: false, ...data } as const;
  } catch (err) {
    console.error("Error fetching testnet points: ", err);
  }
};
